import React, { useRef, useState } from "react";
import { CalendarDays, Clock, MapPin, Sparkles, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { BotanicalBand } from "./Botanical";
import { ChampionAvatar } from "./ChampionAvatar";
import { getRankStyle } from "../lib/rankStyle";
import { EVENT } from "../data/content";

const FACTS = [
  { icon: CalendarDays, label: "Date", value: EVENT.date },
  { icon: Clock, label: "Time", value: EVENT.time },
  { icon: MapPin, label: "Venue", value: `${EVENT.venue}` },
  { icon: Sparkles, label: "Attire", value: "Black Tie Optional" },
];

// Ordered by name
const MARKETPLACE_PARTNERS = [
  { name: "Daniella Ortiz", logo: "/brand/partner-daniella-ortiz.webp", url: "https://daniellaortiz.com", slug: "daniella-ortiz" },
  { name: "Deborah Gaspar", logo: "/brand/partner-deborah-gaspar.webp", url: "https://www.deborahgaspar.com", slug: "deborah-gaspar" },
  { name: "Gorgeous Things", logo: "/brand/partner-gorgeous-things.webp", url: "https://shopgorgeousthings.com", slug: "gorgeous-things" },
  { name: "James Paul", logo: "/brand/partner-james-paul.webp", url: "https://jamespaulcheung.com", slug: "james-paul" },
  { name: "LaCosta Organic Jewelry", logo: "/brand/partner-lacosta.webp", url: "https://lacostaorganicjewelry.com", slug: "lacosta" },
  { name: "Marotta Boutique", logo: "/brand/partner-marotta.webp", url: "https://marottaboutique.com", slug: "marotta" },
];


const IntroFilm = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setMuted(v.muted);
  };

  const restart = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  };

  // Re-mute on every restart so sound must be re-enabled each loop
  const handleEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    setMuted(true);
    v.currentTime = 0;
    v.play().catch(() => {});
  };

  return (
    <div className="relative w-full max-w-[340px]" data-testid="event-intro-film">
        {/* Gilded frame */}
        <div className="rounded-xl bg-gradient-to-br from-mds-gold/70 via-mds-gold/30 to-mds-gold/70 p-[2px] shadow-[0_28px_70px_-25px_rgba(20,25,45,0.55)]">
          <div className="group relative overflow-hidden rounded-[10px] bg-mds-navy">
            <video
              ref={videoRef}
              className="aspect-[9/16] w-full object-cover"
              poster="/brand/mds-intro-poster.jpg"
              autoPlay
              muted
              playsInline
              preload="metadata"
              onEnded={handleEnded}
              data-testid="event-intro-video"
            >
              <source src="/brand/mds-intro-opt.mp4" type="video/mp4" />
            </video>
            {/* Inset hairline + soft vignette */}
            <div className="pointer-events-none absolute inset-0 rounded-[10px] ring-1 ring-inset ring-white/10" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/55 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />

            {/* Restart */}
            <button
              type="button"
              onClick={restart}
              aria-label="Restart video"
              className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-black/45 text-mds-cream backdrop-blur-md transition-colors duration-300 hover:border-mds-gold hover:text-mds-gold lg:h-9 lg:w-9"
              data-testid="event-intro-restart"
            >
              <RotateCcw className="h-4 w-4" strokeWidth={1.6} />
            </button>

            {/* Sound toggle */}
            <button
              type="button"
              onClick={toggleSound}
              aria-label={muted ? "Unmute video" : "Mute video"}
              className={`absolute bottom-4 left-1/2 flex min-h-[44px] -translate-x-1/2 items-center gap-2 rounded-full border px-4 py-2 font-sans text-[11px] uppercase tracking-[0.16em] backdrop-blur-md transition-colors duration-300 lg:min-h-0 ${
                muted
                  ? "sound-cta-pulse border-mds-gold bg-black/55 text-mds-gold"
                  : "border-white/25 bg-black/40 text-mds-cream hover:border-mds-gold hover:text-mds-gold"
              }`}
              data-testid="event-intro-sound-toggle"
            >
              {muted ? <VolumeX className="h-4 w-4" strokeWidth={1.4} /> : <Volume2 className="h-4 w-4" strokeWidth={1.4} />}
              {muted ? "Tap for sound" : "Sound on"}
            </button>
          </div>
        </div>
    </div>
  );
};

export const EventDetails = () => {
  return (
    <section id="event" className="relative bg-mds-paper" data-testid="event-details-section">
      <BotanicalBand className="w-full" />

      <div className="mx-auto max-w-[1200px] px-5 pt-24 pb-14 sm:px-8 sm:pt-32 sm:pb-16">
        <Reveal className="text-center">
          <p className="mt-2 pb-6 pt-4 font-script text-6xl leading-[1.3] text-gold sm:text-8xl">An Extraordinary</p>
          <h2 className="mt-1 font-display smallcaps text-4xl font-medium tracking-tight text-mds-ink sm:text-6xl">
            Evening Awaits
          </h2>
          <p className="mx-auto mt-7 max-w-3xl font-body text-base leading-relaxed text-mds-inksoft">
            Join us as we gather beneath the stars for an inaugural evening of generosity and spectacle. Your experience
            begins with a <b>red carpet arrival</b> worthy of a Hollywood premiere, complete with flashing lights and
            live interviews, before giving way to an elegant <b>social hour</b> featuring curated bars, live
            entertainment, and a <b>luxury curated marketplace</b> showcasing elevated fashion brands from across the
            country alongside distinctive favorites from our local community. As night falls, guests will enjoy a{" "}
            <b>chef-curated dinner</b> served beside the green beneath a star-filled sky while experiencing firsthand the
            life-changing impact of Naples Cancer Advisors. Then, in a moment no one will soon forget, six guests will be
            randomly selected for the opportunity to take the <b>Million Dollar Shot</b> and the chance to win one
            million dollars. The evening will conclude with an{" "}
            <b>unforgettable interactive performance by acclaimed magician and mentalist Alex Geiser</b>, whose
            extraordinary blend of illusion, mind reading, and audience participation promises an experience you will not
            soon forget. <b>Every dollar raised helps provide expert cancer guidance and compassionate support to our
            neighbors throughout Southwest Florida.</b>
          </p>
        </Reveal>

        {/* First-look film + stacked facts */}
        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col items-center justify-center gap-8 lg:flex-row lg:items-stretch">
            <IntroFilm />
            <div className="flex w-full max-w-[340px] flex-col divide-y divide-mds-line overflow-hidden rounded-sm border border-mds-line lg:w-[320px]">
              {FACTS.map((f) => (
                <div
                  key={f.label}
                  className="flex flex-1 flex-col items-center justify-center bg-mds-cream px-7 py-6 text-center"
                  data-testid={`event-fact-${f.label.toLowerCase()}`}
                >
                  <f.icon className="mb-3 h-5 w-5 text-mds-gold" strokeWidth={1.3} />
                  <p className="subhead text-[10px]">{f.label}</p>
                  <p className="mt-1.5 font-display smallcaps text-xl leading-tight text-mds-ink">{f.value}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Reserve CTA */}
        <div id="tickets">
          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-col items-center gap-4 text-center sm:mt-16">
              {/* Marketplace Partners */}
              <div className="mb-8 flex w-full flex-col items-center gap-7 sm:mb-10">
                <h3 className="font-display smallcaps text-2xl font-medium tracking-tight text-mds-ink sm:text-3xl">
                  Meet our Marketplace Partners
                </h3>
                <div
                  className="grid w-full max-w-3xl grid-cols-2 items-center gap-x-10 gap-y-9 sm:grid-cols-3 sm:gap-x-12"
                  data-testid="marketplace-partners"
                >
                  {MARKETPLACE_PARTNERS.map((p) => (
                    <a
                      key={p.slug}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center transition-opacity duration-300 hover:opacity-70"
                      data-testid={`marketplace-link-${p.slug}`}
                    >
                      <img
                        src={p.logo}
                        alt={p.name}
                        className="max-h-11 w-auto max-w-full object-contain sm:max-h-14"
                        draggable={false}
                        data-testid={`marketplace-logo-${p.slug}`}
                      />
                    </a>
                  ))}
                </div>
              </div>

              {/* Emcee & Master of Ceremonies */}
              <div className="mb-2 w-full max-w-4xl border-t border-mds-line pt-14" data-testid="committee-emcee">
                <p className="text-center font-display smallcaps text-3xl font-medium tracking-tight text-mds-ink sm:text-5xl">
                  Emcee &amp; Master of Ceremonies
                </p>
                <div className="mt-9 flex flex-col items-center gap-8 text-center sm:flex-row sm:items-start sm:gap-12 sm:text-left">
                  <div className="flex-shrink-0">
                    <ChampionAvatar
                      champion={{ name: "Peter Busch", photo: "/brand/committee-peter-busch.webp", initials: "PB" }}
                      ringStyle={getRankStyle(null).ringStyle}
                      sizeClass="h-40 w-40 sm:h-48 sm:w-48"
                      textClass="text-4xl"
                    />
                  </div>
                  <div>
                    <h3 className="font-display smallcaps text-2xl leading-tight text-mds-ink sm:text-3xl">Peter Busch</h3>
                    <p className="mt-4 font-body text-base leading-relaxed text-mds-inksoft">
                      <b>Our emcee and master of ceremonies, Peter Busch, is an Emmy Award-winning journalist and evening
                      anchor for Gulf Coast News,</b> where he brings Southwest Florida the news each night at 5, 5:30, 6,
                      and 11 p.m. During his distinguished career, Peter has earned four Emmy Awards, a prestigious
                      national Edward R. Murrow Award, and two regional Murrow Awards for his outstanding reporting from
                      Florida, Arizona, Brazil, and Puerto Rico.
                    </p>
                    <p className="mt-4 font-body text-base leading-relaxed text-mds-inksoft">
                      Beyond the newsroom, Peter is known around the world for his brilliantly pun-filled &ldquo;Dad Joke
                      of the Day&rdquo; on social media, delighting hundreds of thousands of followers with humor that is
                      equal parts clever, groan-worthy, and unforgettable. Fluent in Spanish after living in Mexico City,
                      Peter graduated with honors from the University of Arizona, where he was also a member of the swim
                      team. He and his three daughters proudly call Lee County home.
                    </p>
                  </div>
                </div>
              </div>
              {/* Reserve invitation */}
              <div className="mb-2 flex w-full flex-col items-center" data-testid="reserve-invite">
                <p className="w-full px-6 py-4 text-center font-script text-5xl leading-[1.6] text-gold sm:text-7xl">Join Us Under the Stars</p>
                <h3 className="mt-1 font-display smallcaps text-2xl font-medium tracking-tight text-mds-ink sm:text-3xl">
                  Reserve Your Table or Seat Today
                </h3>
              </div>
              <div className="flex flex-col items-center gap-4 sm:flex-row">
                <a
                  href="https://secure.qgiv.com/event/mdsnaples2026"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-mds-gold px-9 py-3.5 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-mds-cream transition-colors duration-300 hover:bg-mds-greendeep"
                  data-testid="event-reserve-table"
                >
                  Reserve Your Table
                </a>
                <a
                  href="https://secure.qgiv.com/event/mdsnaples2026"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-mds-gold px-9 py-3.5 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-mds-ink transition-colors duration-300 hover:bg-mds-gold hover:text-mds-cream"
                  data-testid="event-reserve-seat"
                >
                  Reserve Your Seat
                </a>
              </div>
              <p className="gold-glimmer font-sans text-[11px] font-extrabold uppercase tracking-[0.16em]" data-testid="event-soldout-note">
                Limited Tables and Individual Seats Remaining — VIP Tables and VIP Seats SOLD OUT
              </p>
              <p className="font-sans text-[11px] uppercase tracking-[0.16em] text-mds-inksoft">{EVENT.ticketsOpen}</p>
            </div>
          </Reveal>
        </div>
      </div>

      <BotanicalBand flip className="w-full" />
    </section>
  );
};
